/**
 * English UI catalogue for namespace 'ui' (W1-12 ui-kit-i18n): strings of the design-system primitives and the
 * #/dev/ui gallery. Imports only src/i18n/define.ts (catalogue zone, ARCHITECTURE.md §4).
 * Vocabulary follows DESIGN.md §2.5 ("Keyframe", "Stay, then jump", community terms in tooltips).
 * Every key is used by src/ui: src/ui/Gallery.test.tsx fails on unused keys, and src/i18n/catalogues.test.ts fails
 * on keys used in source that are missing here.
 */

import { catalogOf, defineMessages } from '../i18n/define';

export const m = defineMessages('ui', {
  // ---------- common ----------
  close: 'Close',
  mixed: 'Mixed',
  mixedValues: 'Mixed values',
  moreInfo: 'More information',

  // ---------- badges ----------
  'badge.inUse': 'In use',
  'badge.inUse.tip': 'This uses a feature that is hidden at the Guided level. It still works and is exported.',
  'badge.phiraIgnores': 'Phira ignores',
  'badge.phiraIgnores.tip': 'Phira does not read this field. It is kept so the chart still opens the same way in RPE.',
  'badge.rpe17': 'RPE ≥ 1.7',
  'badge.rpe17.tip': 'Introduced in RPE 1.7. Older RPE versions ignore it; Phira supports it.',
  'badge.editorOnly': 'Editor only',
  'badge.editorOnly.tip': 'Saved with the project (phiedit.json) and never written into the chart.',
  'badge.baked': 'Baked on export',
  'badge.baked.tip': 'Exported differently: converted into plain RPE events when the chart is written.',
  'badge.full': 'Full',
  'badge.full.tip': 'Part of the Full level. Switch the level in the top bar to see it everywhere.',
  'badge.problem.tip': 'The checker found a problem here. Click to open Problems.',
  'badge.described': '{label}: {tip}',
  'severity.fatal': 'Fatal',
  'severity.error': 'Error',
  'severity.warning': 'Warning',
  'severity.tip': 'Tip',

  // ---------- info popover ----------
  'info.rpe': 'RPE: {term}',
  'info.community': 'Community: {term}',
  'illustration.cover': 'Notes below the line are hidden',
  'illustration.rotateWithParent': 'The child line turns with its parent',
  'illustration.side': 'Notes fall from above or below the line',
  'illustration.stayThenJump': 'The value stays, then jumps at the next keyframe',
  'illustration.easingSlice': 'Only part of the curve is used',
  'illustration.fake': 'A decoration note is drawn but never judged',
  'illustration.speed': 'Scroll speed changes how fast notes approach',
  'illustration.layers': 'Layers add up to the final value',

  // ---------- number field ----------
  'number.scrubHint': 'Drag to change. Hold Shift for bigger steps, Alt for finer steps.',
  'number.mixedHint': 'Type one value for all, or an expression such as +=10, *-1 or lerp(-400, 400, u)',
  'number.result': '= {value}',
  'number.random': 'Random: each item gets its own value when you apply it',
  'number.randomSeed': 'Random: each item gets its own value (seed {seed})',
  'number.resultSeed': '= {value} (random, seed {seed})',
  'number.invalid': 'Type a number or an expression',
  'number.unitToggle': 'Show values in {unit}',
  'number.unitToggleCurrent': 'Values shown in {unit}. Click to show them in {other}.',

  // ---------- beat field ----------
  'beat.hint': 'Beats such as 12:3/4, 12 3/4 or 49/4; +1/4 moves by a quarter beat; 23.5s is a time in seconds',
  'beat.relative': 'Moves every item by {delta}',
  'beat.rounded': '{echo} (rounded by {ms, number, 0.000} ms)',

  // ---------- colour field ----------
  'color.none': 'None (default)',
  'color.pick': 'Choose a colour',
  'color.presets': 'Common colours',
  'color.hex': 'Hex',
  'color.hexPlaceholder': '#RRGGBB',
  'color.red': 'Red',
  'color.green': 'Green',
  'color.blue': 'Blue',
  'color.invalidHex': 'Type a colour as #RRGGBB, for example #FF8800',
  'color.channelRange': 'Use a whole number from 0 to 255',
  'color.system': 'More colours',
  'color.value': 'Colour {hex}',
  'color.noneValue': 'None (default)',

  // ---------- curve thumbnail ----------
  'curve.label': 'Curve',
  'curve.hold': 'Stay, then jump',
  'curve.linear': 'Linear',
  'curve.bezier': 'Custom curve (Bezier)',
  'curve.tween': '{name} (RPE {id})',
  'curve.slice': '{name}, part {left} to {right}',
  'curve.invalid': 'Phira cannot use this part of the curve',

  // ---------- layout ----------
  'splitter.resize': 'Resize',
  'progress.label': 'Progress',
  'progress.percent': '{value, number, percent}',
  'list.empty': 'No items',

  // ---------- gallery (#/dev/ui) ----------
  'gallery.title': 'Design system',
  'gallery.intro': 'Every primitive of src/ui in its states, for visual and keyboard checks.',
  'gallery.theme': 'Theme',
  'gallery.theme.dark': 'Dark',
  'gallery.theme.light': 'Light',
  'gallery.theme.highContrast': 'High contrast',
  'gallery.language': 'Language',
  'gallery.reducedMotion': 'Reduce motion',
  'gallery.buttons': 'Buttons',
  'gallery.primary': 'Export',
  'gallery.secondary': 'Preview',
  'gallery.ghost': 'Cancel',
  'gallery.danger': 'Delete line',
  'gallery.disabled': 'Not available',
  'gallery.disabledReason': 'Select a keyframe first',
  'gallery.apply': 'Apply',
  'gallery.play': 'Play',
  'gallery.snap': 'Beat snap',
  'gallery.undo': 'Undo',
  'gallery.badges': 'Badges',
  'gallery.fields': 'Fields',
  'gallery.speed': 'Speed',
  'gallery.speedHelp': 'Multiplies how far the notes travel.',
  'gallery.positionX': 'X',
  'gallery.rotation': 'Rotation',
  'gallery.opacity': 'Opacity',
  'gallery.time': 'Time',
  'gallery.noteColour': 'Note colour',
  'gallery.lineColour': 'Tag colour',
  'gallery.name': 'Name',
  'gallery.namePlaceholder': 'Line name',
  'gallery.notes': 'Notes',
  'gallery.json': 'Raw JSON',
  'gallery.unitTimes': 'x',
  'gallery.unitPx': 'px',
  'gallery.unitDeg': 'deg',
  'gallery.unitRaw': '0-255',
  'gallery.unitPercent': '%',
  'gallery.choices': 'Choices',
  'gallery.side': 'Side',
  'gallery.sideTop': 'Top',
  'gallery.sideBottom': 'Bottom',
  'gallery.noteType': 'Type',
  'gallery.tap': 'Tap',
  'gallery.drag': 'Drag',
  'gallery.flick': 'Flick',
  'gallery.hold': 'Hold',
  'gallery.fake': 'Decoration only: not judged, no sound',
  'gallery.mixedCheckbox': 'Mixed selection',
  'gallery.cover': 'Hide notes under the line',
  'gallery.coverInfo': 'Notes below the line are not drawn while they are under it.',
  'gallery.coverRpeTerm': 'Cover (isCover)',
  'gallery.coverCommunityTerm': '遮罩',
  'gallery.metronome': 'Metronome',
  'gallery.volume': 'Volume',
  'gallery.overlays': 'Overlays',
  'gallery.openDialog': 'Open dialog',
  'gallery.dialogTitle': 'Delete 3 lines?',
  'gallery.dialogBody': 'The lines and their notes are removed. You can undo this.',
  'gallery.openSheet': 'Open side sheet',
  'gallery.sheetTitle': 'Distribute',
  'gallery.sheetBody': 'The timeline and preview stay usable while this sheet is open.',
  'gallery.popover': 'Popover',
  'gallery.popoverBody': 'Popovers hold small forms and pickers.',
  'gallery.tooltip': 'Hover or focus me',
  'gallery.tooltipBody': 'Tooltips show the shortcut of their command.',
  'gallery.menu': 'Menu',
  'gallery.contextMenu': 'Right-click here',
  'gallery.copy': 'Copy',
  'gallery.paste': 'Paste',
  'gallery.more': 'More',
  'gallery.mirror': 'Mirror',
  'gallery.reverse': 'Reverse',
  'gallery.tabs': 'Tabs',
  'gallery.tabLines': 'Lines',
  'gallery.tabProblems': 'Problems',
  'gallery.tabHistory': 'History',
  'gallery.tabContent': 'Content of the {tab} tab.',
  'gallery.problemCount': '{count, plural, one {# problem} other {# problems}}',
  'gallery.lists': 'Lists and layout',
  'gallery.listLabel': 'Lines',
  'gallery.lineName': 'Line {index}',
  'gallery.section': 'Section',
  'gallery.sectionBody': 'Sections collapse with their header button.',
  'gallery.toolbar': 'Tools',
  'gallery.select': 'Select',
  'gallery.place': 'Place notes',
  'gallery.erase': 'Eraser',
  'gallery.progress': 'Exporting',
  'gallery.empty': 'No problems found',
  'gallery.emptyBody': 'The checker runs after every edit.',
  'gallery.emptyAction': 'Run the checker',
  'gallery.curves': 'Curves',
  'gallery.swatches': 'Swatches',
});

export default catalogOf(m);
